/**
 * 📅 CALENDAR ENGINE (Visual Time System)
 * 
 * Purpose: What the user sees
 * 
 * This system answers:
 * - Which days are highlighted?
 * - Which days are selectable?
 * - Is a date blocked?
 * - What range did the user pick?
 * 
 * ⚠️ IMPORTANT: This system does NOT know anything about pricing.
 * It works with visual date ranges for UI display only.
 * 
 * Rules:
 * - Date range selection: Inclusive (30–31 shows 2 days highlighted)
 * - Date display: Based on user timezone
 * - Availability grid: Day blocks
 * - UI range labels: "Jan 30 – Jan 31"
 * - Hover/selection UX: Visual continuity
 */

import { differenceInCalendarDays, startOfDay, isBefore, isAfter, isSameDay } from 'date-fns';

/**
 * Calendar range type - what the user selects in the UI
 */
export type CalendarRange = {
  startDate: Date;
  endDate: Date;
};

/**
 * Calculate rental duration in nights (for pricing and display).
 *
 * Rental logic: 1 March → 2 March = 1 night = 1 day of rental (not 2).
 *
 * Formula: differenceInCalendarDays(end, start), minimum 1.
 * Uses calendar days only (timezone-safe, no time-of-day).
 *
 * Examples:
 * - 1 Mar → 2 Mar = 1 night (1 day)
 * - 1 Mar → 3 Mar = 2 nights (2 days)
 * - Same day = 1 night minimum
 */
export function calculateCalendarDuration(range: CalendarRange): number {
  const start = startOfDay(range.startDate);
  const end = startOfDay(range.endDate);

  if (isBefore(end, start)) {
    return 0;
  }

  const nights = differenceInCalendarDays(end, start);
  return Math.max(1, nights);
}

/**
 * Check if a date is within a calendar range (inclusive)
 */
export function isDateInCalendarRange(date: Date, range: CalendarRange): boolean {
  const normalizedDate = startOfDay(date);
  const normalizedStart = startOfDay(range.startDate);
  const normalizedEnd = startOfDay(range.endDate);
  
  return (
    (isSameDay(normalizedDate, normalizedStart) ||
      isAfter(normalizedDate, normalizedStart)) &&
    (isSameDay(normalizedDate, normalizedEnd) ||
      isBefore(normalizedDate, normalizedEnd))
  );
}

/**
 * Format calendar range for display
 */
export function formatCalendarRange(range: CalendarRange): string {
  const start = startOfDay(range.startDate);
  const end = startOfDay(range.endDate);
  
  // Format as "Jan 30 – Jan 31" or "Jan 30 – Feb 1"
  const startStr = start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  const endStr = end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  
  return `${startStr} – ${endStr}`;
}

/**
 * Validate calendar range selection
 */
export function validateCalendarRange(range: CalendarRange): {
  isValid: boolean;
  error?: string;
} {
  const start = startOfDay(range.startDate);
  const end = startOfDay(range.endDate);
  
  if (isBefore(end, start)) {
    return {
      isValid: false,
      error: 'La date de fin doit être après la date de début',
    };
  }
  
  if (isSameDay(start, end)) {
    return {
      isValid: true, // Same day is valid for calendar selection
    };
  }
  
  return { isValid: true };
}
